import { useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import type { CareSchedule, CareActionType } from '@/types';
import { everyNDays } from '@/utils/date';
import * as api from '@/utils/api';

interface CareScheduleEditorProps {
  isOpen: boolean;
  onClose: () => void;
  plantId: string;
  existingSchedules: CareSchedule[];
  onUpdated: () => void;
}

const allActions: { type: CareActionType; emoji: string; label: string }[] = [
  { type: 'WATERING', emoji: '💧', label: 'Полив' },
  { type: 'FERTILIZING', emoji: '🧪', label: 'Удобрение' },
  { type: 'MISTING', emoji: '💨', label: 'Опрыскивание' },
  { type: 'ROTATING', emoji: '🔄', label: 'Поворот' },
  { type: 'REPOTTING', emoji: '🪴', label: 'Пересадка' },
  { type: 'PRUNING', emoji: '✂️', label: 'Обрезка' },
  { type: 'CLEANING', emoji: '🧹', label: 'Чистка листьев' },
  { type: 'OTHER', emoji: '📝', label: 'Другое' },
];

const defaultFrequencies: Record<CareActionType, number> = {
  WATERING: 7,
  FERTILIZING: 14,
  MISTING: 3,
  ROTATING: 14,
  REPOTTING: 180,
  PRUNING: 30,
  CLEANING: 14,
  OTHER: 7,
};

interface RowState {
  isEnabled: boolean;
  frequencyDays: number;
}

export default function CareScheduleEditor({
  isOpen,
  onClose,
  plantId,
  existingSchedules,
  onUpdated,
}: CareScheduleEditorProps) {
  const [rows, setRows] = useState<Record<CareActionType, RowState>>(() => buildInitialState(existingSchedules));
  const [saving, setSaving] = useState<CareActionType | null>(null);

  // Rebuild state whenever the sheet opens with fresh data
  useEffect(() => {
    if (isOpen) {
      setRows(buildInitialState(existingSchedules));
    }
  }, [isOpen, existingSchedules]);

  const handleToggle = useCallback(async (actionType: CareActionType) => {
    const current = rows[actionType];
    const newEnabled = !current.isEnabled;

    // Optimistic update
    setRows((prev) => ({
      ...prev,
      [actionType]: { ...prev[actionType], isEnabled: newEnabled },
    }));

    setSaving(actionType);
    try {
      await api.upsertCareSchedule(plantId, {
        actionType,
        frequencyDays: current.frequencyDays,
        isEnabled: newEnabled,
      });
      onUpdated();
    } catch {
      // Revert on error
      setRows((prev) => ({
        ...prev,
        [actionType]: { ...prev[actionType], isEnabled: current.isEnabled },
      }));
    } finally {
      setSaving(null);
    }
  }, [rows, plantId, onUpdated]);

  const handleFrequencyChange = useCallback((actionType: CareActionType, value: string) => {
    const num = parseInt(value, 10);
    if (value === '' || (num >= 0 && num <= 999)) {
      setRows((prev) => ({
        ...prev,
        [actionType]: { ...prev[actionType], frequencyDays: num || 0 },
      }));
    }
  }, []);

  const handleFrequencyBlur = useCallback(async (actionType: CareActionType) => {
    const current = rows[actionType];
    const freq = current.frequencyDays;

    // Only save if enabled and frequency is valid
    if (freq < 1) {
      // Reset to default if invalid
      setRows((prev) => ({
        ...prev,
        [actionType]: { ...prev[actionType], frequencyDays: defaultFrequencies[actionType] },
      }));
      return;
    }

    // Find matching existing schedule to see if we actually changed
    const existing = existingSchedules.find((s) => s.actionType === actionType);
    if (existing && existing.frequencyDays === freq && existing.isEnabled === current.isEnabled) {
      return; // No change
    }

    setSaving(actionType);
    try {
      await api.upsertCareSchedule(plantId, {
        actionType,
        frequencyDays: freq,
        isEnabled: current.isEnabled,
      });
      onUpdated();
    } catch {
      // Revert on error
      if (existing) {
        setRows((prev) => ({
          ...prev,
          [actionType]: { ...prev[actionType], frequencyDays: existing.frequencyDays },
        }));
      }
    } finally {
      setSaving(null);
    }
  }, [rows, plantId, existingSchedules, onUpdated]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center"
      style={{ background: 'rgba(0, 0, 0, 0.4)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div className="glass-sheet w-full max-h-[85vh] rounded-t-3xl flex flex-col animate-slide-up" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-4 py-4 border-b border-[var(--sf-border-light)] flex items-center justify-between flex-shrink-0">
          <h3 className="font-semibold text-lg">Расписание ухода</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full glass-circle flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Schedule rows */}
        <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 space-y-2" style={{ WebkitOverflowScrolling: 'touch' }}>
          {allActions.map((action) => {
            const row = rows[action.type];
            const isSaving = saving === action.type;

            return (
              <div
                key={action.type}
                className={`tg-card flex items-center gap-3 transition-opacity ${isSaving ? 'opacity-70' : ''}`}
              >
                {/* Emoji + label */}
                <span className="text-xl flex-shrink-0">{action.emoji}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">{action.label}</p>
                  {row.isEnabled && (
                    <p className="text-xs text-tg-hint">
                      {row.frequencyDays ? everyNDays(row.frequencyDays).toLowerCase() : '—'}
                    </p>
                  )}
                </div>

                {/* Frequency input */}
                {row.isEnabled && (
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={row.frequencyDays || ''}
                      onChange={(e) => handleFrequencyChange(action.type, e.target.value.replace(/\D/g, ''))}
                      onBlur={() => handleFrequencyBlur(action.type)}
                      className="w-14 text-center text-sm py-1.5 px-2 rounded-lg font-medium"
                      style={{ background: 'var(--sf-bg)', border: '1px solid var(--sf-border-light)' }}
                    />
                    <span className="text-xs text-tg-hint">дн.</span>
                  </div>
                )}

                {/* Toggle */}
                <button
                  onClick={() => handleToggle(action.type)}
                  disabled={isSaving}
                  className="w-[52px] h-8 rounded-full flex-shrink-0 p-1 transition-colors duration-300"
                  style={{
                    background: row.isEnabled
                      ? 'var(--plant-green-500, #22c55e)'
                      : 'var(--sf-border-light, #d1d5db)',
                  }}
                >
                  <span
                    className={`block w-6 h-6 rounded-full bg-white transition-transform duration-300`}
                    style={{
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      transform: row.isEnabled ? 'translateX(20px)' : 'translateX(0)',
                    }}
                  />
                </button>
              </div>
            );
          })}

          <p className="text-xs text-tg-hint text-center pt-2 pb-4 safe-area-bottom">
            Включите нужные виды ухода и задайте частоту в днях
          </p>
        </div>
      </div>
    </div>
  );
}

function buildInitialState(schedules: CareSchedule[]): Record<CareActionType, RowState> {
  const state = {} as Record<CareActionType, RowState>;
  for (const action of allActions) {
    const existing = schedules.find((s) => s.actionType === action.type);
    if (existing) {
      state[action.type] = {
        isEnabled: existing.isEnabled,
        frequencyDays: existing.frequencyDays,
      };
    } else {
      state[action.type] = {
        isEnabled: false,
        frequencyDays: defaultFrequencies[action.type],
      };
    }
  }
  return state;
}
